// SCR-231's standing and history (REQ-087, API-232): one row per (quota, scope instance) with
// what is used of the effective limit as a bar, the state, when it resets, active grants and who
// is refused right now; then the quota event history (warnings crossed, limits reached, grants),
// newest first, a page at a time. Where money is hidden from the viewer only the % shows.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { OrgContext } from "../../api/org-context";
import { Card, Chip } from "../../primitives/page";
import { Empty, ErrorBanner, Loading } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import {
  asApiError,
  eventRows,
  isNotAvailable,
  isStale,
  loadable,
  loadEvents,
  loadStanding,
  NOT_AVAILABLE,
  standingRows,
  utcMoment,
  type EventRow,
  type Loadable,
  type Quota,
  type QuotaEvent,
  type QuotaStandingResponse,
  type StandingRow,
} from "./model";
import type { ApiError } from "../../api/errors";

/** A 0..1 bar, red when exhausted, amber past a warning (SVG: the CSP forbids style attributes). */
export function UsedBar({ fraction, tone, label }: { fraction: number; tone: string; label: string }) {
  const width = Math.max(0, Math.min(1, fraction)) * 90;
  const fill = tone === "red" ? "share-bar-fill quota-fill-red" : tone === "amber" ? "share-bar-fill quota-fill-amber" : "share-bar-fill";
  return (
    <svg class="share-bar" viewBox="0 0 90 6" role="img" aria-label={label}>
      <rect class="share-bar-track" x="0" y="0" width="90" height="6" rx="3" />
      {width > 0 && <rect class={fill} x="0" y="0" width={Math.max(3, width)} height="6" rx="3" />}
    </svg>
  );
}

function standingColumns(withQuota: boolean): Column<StandingRow>[] {
  const columns: Column<StandingRow>[] = [
    { key: "scope", header: "Who", cell: (row) => row.scopeName, compare: (a, b) => a.scopeName.localeCompare(b.scopeName) },
    {
      key: "used",
      header: "Used",
      help: "quotas.utilization",
      cell: (row) => (
        <span class="quota-used">
          <UsedBar fraction={row.fraction} tone={row.stateTone} label={`${row.percent} used`} />
          <span>
            {row.percent}
            {row.amounts && <span class="cell-sub"> · {row.amounts}</span>}
          </span>
        </span>
      ),
      compare: (a, b) => a.fraction - b.fraction,
    },
    { key: "state", header: "State", help: "quotas.state", cell: (row) => <Chip tone={row.stateTone}>{row.stateLabel}</Chip> },
    { key: "resets", header: "Resets", cell: (row) => row.resets },
    { key: "grants", header: "Grants", numeric: true, cell: (row) => row.grants },
    {
      key: "refused",
      header: "Refused now",
      cell: (row) => (row.refused.length === 0 ? "—" : <span class="quota-refused">{row.refused.join(", ")}</span>),
    },
  ];
  if (withQuota) columns.unshift({ key: "quota", header: "Quota", cell: (row) => row.quotaName, compare: (a, b) => a.quotaName.localeCompare(b.quotaName) });
  return columns;
}

/** The standing of every quota of the org, or of one (`quotaId`). */
export function StandingPanel({ context, quotaId, now = new Date() }: { context: OrgContext; quotaId?: string | undefined; now?: Date }) {
  const client = useApi();
  const [state, setState] = useState<Loadable<QuotaStandingResponse>>({ kind: "loading" });
  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadStanding(client, context.orgId, quotaId)).then(setState);
  };
  useEffect(load, [client, context.orgId, quotaId]);

  // asOf is null until a proxy of the org has synced: nothing is stale yet, there is no counter.
  const asOf = state.kind === "loaded" ? state.value.asOf : null;
  const stale = asOf !== null && isStale(asOf, now);
  return (
    <Card
      title="Standing this period"
      help="quotas.utilization"
      actions={state.kind === "loaded" && (asOf === null
        ? <Chip tone="grey" title="No proxy of this organization has synced its counters yet">Never synced</Chip>
        : <Chip tone={stale ? "amber" : "grey"} title={`Counters as of ${utcMoment(asOf)}`}>{stale ? "Stale" : `As of ${utcMoment(asOf)}`}</Chip>)}
    >
      {state.kind === "loading" && <Loading label="Loading standing…" />}
      {state.kind === "unavailable" && <Empty message={NOT_AVAILABLE} />}
      {state.kind === "error" && <ErrorBanner error={state.error} onRetry={load} />}
      {state.kind === "loaded" && (
        <DataTable
          caption="Quota standing"
          hideCaption
          columns={standingColumns(quotaId === undefined)}
          rows={standingRows(state.value, context.moneyPolicy)}
          rowKey={(row) => row.key}
          emptyMessage="No usage counted this period yet."
        />
      )}
    </Card>
  );
}

const EVENT_COLUMNS: readonly Column<EventRow>[] = [
  { key: "when", header: "When", cell: (row) => row.when },
  { key: "quota", header: "Quota", cell: (row) => row.quotaName },
  { key: "what", header: "What happened", cell: (row) => <Chip tone={row.tone}>{row.what}</Chip> },
  { key: "who", header: "Who", cell: (row) => row.who },
  { key: "amounts", header: "Used", cell: (row) => row.amounts ?? "—" },
];

interface EventsState {
  events: QuotaEvent[];
  nextCursor: string | null;
  loading: boolean;
  unavailable: boolean;
  error: ApiError | null;
}

const EVENTS_START: EventsState = { events: [], nextCursor: null, loading: true, unavailable: false, error: null };

/** The quota event history of the last 30 days, a page at a time (keyset cursor). */
export function EventsList({ context, quotas, quotaId }: { context: OrgContext; quotas: readonly Quota[]; quotaId?: string | undefined }) {
  const client = useApi();
  const [state, setState] = useState<EventsState>(EVENTS_START);

  const fetchPage = (cursor: string | null, previous: QuotaEvent[]) => {
    setState((current) => ({ ...current, loading: true, error: null }));
    loadEvents(client, context.orgId, { quotaId, cursor })
      .then((page) => setState({ events: [...previous, ...page.items], nextCursor: page.nextCursor, loading: false, unavailable: false, error: null }))
      .catch((error: unknown) =>
        setState((current) => ({ ...current, loading: false, unavailable: isNotAvailable(error), error: isNotAvailable(error) ? null : asApiError(error) })),
      );
  };
  const reload = () => {
    setState(EVENTS_START);
    fetchPage(null, []);
  };
  useEffect(reload, [client, context.orgId, quotaId]);

  const byId = new Map(quotas.map((quota) => [quota.id, quota]));
  return (
    <Card title="History">
      {state.unavailable ? (
        <Empty message={NOT_AVAILABLE} />
      ) : (
        <DataTable
          caption="Quota events"
          hideCaption
          columns={EVENT_COLUMNS}
          rows={eventRows(state.events, byId, context.moneyPolicy)}
          rowKey={(row) => row.key}
          loading={state.loading}
          error={state.error}
          onRetry={reload}
          emptyMessage="No warnings, refusals or grants in the last 30 days."
          nextCursor={state.nextCursor}
          onLoadMore={() => fetchPage(state.nextCursor, state.events)}
        />
      )}
    </Card>
  );
}
